import React, { useState } from 'react';
import { Heart, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Separator } from '../ui/separator';
import type { DoorConfiguration } from '../types/doorConfiguration';
import {
  colourLabel,
  FRAME_LABELS,
  furnitureLabel,
  glassLabel,
  slabStyleLabel,
} from './configurationLabels';
import { LiveDoorPreview } from './LiveDoorPreview';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export const DesignSummary: React.FC<{
  configuration: DoorConfiguration;
  onStartAgain: () => void;
}> = ({ configuration, onStartAgain }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const confirmStartAgain = () => {
    setConfirmOpen(false);
    onStartAgain();
  };

  return (
  <aside className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card">
    <div className="border-b border-border px-4 py-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide">Your design</h2>
    </div>
    <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
      <div className="mb-3 overflow-hidden rounded-lg border border-border bg-muted">
        <LiveDoorPreview configuration={configuration} compact />
      </div>
      <Row label="Frame" value={FRAME_LABELS[configuration.frameProfile]} />
      <Row
        label="Size"
        value={`${configuration.dimensions.overallWidthMm} × ${configuration.dimensions.overallHeightMm} mm`}
      />
      <Row label="Style" value={slabStyleLabel(configuration.slab.styleId)} />
      <Row
        label="Colour"
        value={`${colourLabel(configuration.slab.colourId)} / inside ${colourLabel(configuration.slab.internalColourId)}`}
      />
      <Row label="Cill" value={configuration.ancillaries.hasCill ? 'Cill' : 'No cill'} />
      <Row label="Glass" value={glassLabel(configuration.glazing)} />
      <Row label="Furniture" value={furnitureLabel(configuration.hardware)} />
      <Separator className="my-3" />
      <p className="text-xs text-muted-foreground">
        Pricing and saving will appear here when quote handoff is wired.
      </p>
    </div>
    <div className="flex flex-col gap-2 border-t border-border p-4">
      <Button type="button" disabled title="Saving will be available when quote handoff is wired">
        <Heart className="h-4 w-4" />
        Save design
      </Button>
      <Button type="button" variant="outline" onClick={() => setConfirmOpen(true)}>
        <RotateCcw className="h-4 w-4" />
        Start again
      </Button>
    </div>
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start again?</DialogTitle>
          <DialogDescription>
            Are you sure? Your current design choices will be lost. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={confirmStartAgain}>
            Yes, start again
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </aside>
  );
};
